import { Card, CardContent } from '@/components/ui/card';
import { Clock9, Send } from 'lucide-react';
import React from 'react';

export interface WhatsAppMessage {
  id: string;
  threadId?: string | null;
  from: string;
  messageId: string;
  message: string;
  to: string;
  templateName: string;
  parameters: string[];
  messageTimestamp: string;
  phoneNumberId?: string;
  languageCode?: string;
  read: boolean;
  messages?: WhatsAppMessage[];
}

export interface WhatsAppListProps {
  messages: WhatsAppMessage[];
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  onSelectMessage: (messageId: string) => void;
  selectedMessageId: string | null;
  onOpenWhatsAppThreadDialog: (open: boolean) => void;
  orgPhoneNumbers: { phoneNumber: string; phoneNumberId: string }[];
}

const getMessageNumbers = (msg: WhatsAppMessage, orgPhoneNumbers: { phoneNumber: string; phoneNumberId: string }[]) => {
  // Create a stable mapping of phoneNumberId to phoneNumber
  const phoneNumberIdToPhoneNumber: Record<string, string> = {};
  orgPhoneNumbers.forEach(phone => {
    phoneNumberIdToPhoneNumber[phone.phoneNumberId] = phone.phoneNumber;
  });

  // Create a stable set of organization phone numbers for quick lookup
  const orgPhoneNumberSet = new Set(orgPhoneNumbers.map(phone => phone.phoneNumber));

  // Check if the from field is a phoneNumberId (organization message)
  const isFromOrg = msg.phoneNumberId && msg.from === msg.phoneNumberId;
  
  // Check if the from field matches an org phone number (incoming org message)
  const isFromOrgPhoneNumber = orgPhoneNumberSet.has(msg.from);
  const fromIsOrg = isFromOrg || isFromOrgPhoneNumber;

  // Get the actual phone numbers for display
  const getDisplayNumber = (value: string): string => {
    // If it's a phoneNumberId, convert it to the actual phone number
    if (phoneNumberIdToPhoneNumber[value]) {
      return phoneNumberIdToPhoneNumber[value];
    }
    return value;
  };

  return {
    customer: fromIsOrg ? getDisplayNumber(msg.to) : getDisplayNumber(msg.from),
    organization: fromIsOrg ? getDisplayNumber(msg.from) : getDisplayNumber(msg.to)
  };
}

const isPhoneNumber = (value: string): boolean => {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  return phoneRegex.test(value);
};

const formatDisplayName = (value: string): string => {
  if (isPhoneNumber(value)) {
    return value.startsWith('+') ? value : `+${value}`;
  }
  return value;
};

const WhatsAppList: React.FC<WhatsAppListProps> = ({
  messages,
  searchTerm,
  onSearchTermChange,
  onSelectMessage,
  selectedMessageId,
  onOpenWhatsAppThreadDialog,
  orgPhoneNumbers,
}) => {

  return (
    <div className="flex-1 space-y-3 overflow-y-auto">
      {messages.length < 1 ? (
        <div className="flex h-full items-center justify-center text-sm text-gray-400">
          No WhatsApp messages found
        </div>
      ) : (
        messages.map(msg => {
          const { customer, organization } = getMessageNumbers(msg, orgPhoneNumbers);

          return (
            <Card
              key={msg.id}
              onClick={() => {
                onSelectMessage(msg.id);
                onOpenWhatsAppThreadDialog(true);
              }}
              className={`relative cursor-pointer hover:bg-gray-50 ${
                selectedMessageId === msg.id ? 'bg-blue-100' : 'bg-white'
              } rounded-md shadow-none`}
            >
              <CardContent className="p-3 pl-6">
                <div className="flex w-full items-start">
                  <div className="mr-2">
                    <Send className="mt-2 h-5 w-5 text-green-500" />
                  </div>
                  <div className="w-full">
                    <div className="mt-1 flex items-center justify-between text-base">
                      <div className="w-[400px] truncate">
                        From: {formatDisplayName(customer)}
                      </div>
                    </div>

                    <div className="mt-1 flex items-center text-xs text-gray-500">
                      <Clock9 className="mr-1 h-3 w-3" />
                      {new Date(msg.messageTimestamp).toLocaleDateString()}{' '}
                      {new Date(msg.messageTimestamp).toLocaleTimeString()}
                    </div>

                    <div className="mt-1 flex items-center text-xs text-gray-500">
                      To: {formatDisplayName(organization)}
                    </div>

                    {msg.messages && msg.messages.length > 1 && (
                      <div className="mt-1 text-xs text-gray-500">
                        {msg.messages.length} messages in thread
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default WhatsAppList;
